import { ComponentProps } from "react";
import { BlockTitle } from "@blocks/Blocks";
import SplideArrows from "@components/Slider/components/SplideArrows";
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from "@greglaisph/react-splide";
import { ImagePresets } from "@pages/Article/components/ArticleThumbnail/utils/enums";
import Card from "@particles/card/Card";
import { Grid } from "@splidejs/splide-extension-grid";
import { cn } from "@util/helpers";

import "@greglaisph/react-splide/css/core";

type PodcastsProps = {
  /** This is the classname of the component. */
  className?: string;
  /** List of articles to be displayed. */
  cardProps: ComponentProps<typeof Card>[];
};

/** The podcasts component in BT SG 60 page.
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79113&m=dev
 */
const Podcasts = ({
  className,
  cardProps,
}: PodcastsProps): React.ReactElement => {
  if (cardProps.length === 0) return <></>;

  const options: Options = {
    pagination: false,
    type: "slide",
    autoplay: false,
    interval: 10000,
    perMove: 1,
    gap: "1rem",
    focus: 0,
    omitEnd: true,
    mediaQuery: "min",
    perPage: 2,
    fixedWidth: "90%",
    arrows: false,
    breakpoints: {
      768: { fixedWidth: "60%", arrows: true },
      992: { fixedWidth: undefined, perMove: 3, perPage: 3 },
    },
  };

  return (
    <div
      data-testid="podcasts-component"
      className={cn("border-t border-gray-850 pt-3", className)}
    >
      <BlockTitle text="PODCASTS" rootClassName="mb-4" />

      <Splide
        options={options}
        extensions={{ Grid }}
        tag="div"
        hasTrack={false}
        className="relative"
      >
        <SplideArrows className="absolute -top-12 right-0 flex flex-row items-center justify-center gap-2" />

        <SplideTrack>
          {cardProps.map((cardProp) => {
            return (
              <SplideSlide key={cardProp.id}>
                <Card
                  {...cardProp}
                  variations={{
                    image: {
                      position: "left",
                      width: "w-[80px]",
                      aspectRatio: cn(
                        "aspect-square object-contain object-center"
                      ),
                      filters: {
                        w: ImagePresets.SMALL,
                        dpr: 1,
                        f: "webp",
                      },
                      orientation: "square",
                    },
                    content: {
                      width: "w-[calc(100%-80px)]",
                      extraClass: "pl-4",
                    },
                    title: {
                      color: "text-gray-850",
                      size: "text-base",
                      extraClass: cn("font-lct font-semibold leading-tight"),
                    },
                    kicker: {
                      color: "text-gray-850",
                      extraClass: cn(
                        "font-poppins text-4xs font-light leading-6 tracking-[1px]s"
                      ),
                    },
                  }}
                />
              </SplideSlide>
            );
          })}
        </SplideTrack>
      </Splide>
    </div>
  );
};

export default Podcasts;
